import _ from 'lodash';
import React from 'react';
import { Helmet } from 'react-helmet';
import FontAwesome from 'react-fontawesome';
import Lightbox from 'react-image-lightbox';

import EmbeddedContent from 'components/pages/default/EmbeddedContent';
import FailsafeImg from 'components/global/application/FailsafeImg';
import InstrumentGroup from 'components/pages/default/InstrumentGroup';
import SlickCarousel from 'components/global/application/SlickCarousel';

import history from 'providers/history';
import { AliasApi, NodeApi, UsersApi } from 'providers/api';

import { Article } from 'types/drupal/ArticleNode';
import { InstrumentGroup as InstrumentGroupNode } from 'types/drupal/InstrumentGroupNode';
import { Page } from 'types/drupal/PageNode';
import { User } from 'types/drupal/User';
import { VintageGuitarArticle } from 'types/drupal/VintageGuitarArticleNode';

import './DefaultPage.sass';

export interface StateProps {
    isMoble: boolean;
}

export interface OwnProps {
    location: Location;
}

type CombinedProps = OwnProps & StateProps;

interface State {
    contentLoaded: boolean;
    content: Page | User | Article | VintageGuitarArticle;
    isLighboxOpen: boolean;
    lightboxIndex: number;
    slideImageIndex: number;
}

class DefaultPage extends React.Component<CombinedProps, State> {
    constructor(props: CombinedProps) {
        super(props);

        this.state = {
            content: null,
            contentLoaded: false,
            isLighboxOpen: false,
            lightboxIndex: 0,
            slideImageIndex: 0
        };
    }

    public componentDidMount() {
        this.loadData();
    }

    public componentDidUpdate() {
        if (this.state.contentLoaded) {
            let targetAlias = this.props.location.pathname.slice(1);
            if (targetAlias.endsWith('/')) targetAlias = targetAlias.slice(0, -1);
            if (targetAlias !== this.state.content.alias) {
                this.setState({ content: null, contentLoaded: false });
                this.loadData();
            }
        }
    }

    public helmet() {
        const { content } = this.state;

        const title = (content as Page | Article | InstrumentGroupNode | VintageGuitarArticle).meta_title;
        const description = (content as Page | Article | InstrumentGroupNode | VintageGuitarArticle).meta_description;
        return (
            <Helmet>
                <meta name="title" content={title} />
                <meta name="description" content={description} />
                <title>{title}</title>
            </Helmet>
        );
    }

    /**
     * Render lightbox for gallery
     */
    public renderLightbox = (images: any) => {
        if (!this.state.isLighboxOpen) {
            return;
        }
        const image = images[this.state.lightboxIndex];
        const nextIndex = (this.state.lightboxIndex + 1) % images.length;
        const nextImage = images[nextIndex];
        const prevIndex = (this.state.lightboxIndex + images.length - 1) % images.length;
        const prevImage = images[prevIndex];

        return (
            <Lightbox
                animationDisabled={true}
                imageTitle={image.title ? image.title : ''}
                mainSrc={image.file.url}
                nextSrc={nextImage.file.url}
                prevSrc={prevImage.file.url}
                onCloseRequest={() => this.setState({ isLighboxOpen: false })}
                onMovePrevRequest={() => this.setState({ lightboxIndex: prevIndex })}
                onMoveNextRequest={() => this.setState({ lightboxIndex: nextIndex })}
            />
        );
    }

    public handleSlideClick = (imageIndex: number) => {
        this.setState({ slideImageIndex: imageIndex });
    }

    public render() {
        const { content, contentLoaded, slideImageIndex } = this.state;
        if (!contentLoaded) return (<FontAwesome className="loading-spinner" name="spinner" size="3x" spin />);

        // User Model doesnt inherit from node and has no type property. Page, Article and instrument group models do
        const articleInstgrp = (content as Page | Article | InstrumentGroupNode | VintageGuitarArticle);
        const isUser = content.alias.startsWith('users/');
        const isNews = !isUser && articleInstgrp.type === 'article';
        const isGroup = !isUser && articleInstgrp.type === 'instrument_group';
        const isVintageGuitar = !isUser && articleInstgrp.type === 'vintageguitars';
        const userContent = (content as User);
        const contentHtml = isUser
            ? userContent.biography
                ? userContent.biography.value
                : null
            : articleInstgrp.hasOwnProperty('body') && articleInstgrp.body
                ? articleInstgrp.body.value
                : '';
        const contentImgUrl = isUser
            ? (content as User).picture.url
            : isNews
                ? _.get(content, 'field_image.file.url')
                : isVintageGuitar
                    ? null
                    : null;
        const hasSlideshow = content.hasOwnProperty('field_instr_image') && content.field_instr_image.length > 0;
        const currentSlideshowImage = hasSlideshow ? content.field_instr_image[slideImageIndex] : null;
        const contentClass = hasSlideshow ? 'col-md-9 col-sm-12' : '';

        return(
            <div className="row DefaultPage-container">
                <div className="col-md-10 offset-md-1">
                    <div className="row">
                        {this.helmet()}
                        {
                            hasSlideshow && (
                                <div className="slick-slideshow col-md-3 col-sm-12">
                                    <FailsafeImg
                                        className="slideshow-main-image"
                                        src={currentSlideshowImage.file.url}
                                        alt={currentSlideshowImage.alt ? currentSlideshowImage.alt : currentSlideshowImage.file.filename}
                                        title={currentSlideshowImage.title ? currentSlideshowImage.title : currentSlideshowImage.file.filename}
                                        onClick={(e) => {
                                            this.setState({
                                                isLighboxOpen: true,
                                                lightboxIndex: slideImageIndex
                                            });
                                        }}
                                    />
                                    <SlickCarousel
                                        imageSources={content.field_instr_image
                                            ? content.field_instr_image.map((k, i) => {
                                                return k.file.url;
                                            })
                                            : ['']}
                                        onClick={this.handleSlideClick}
                                    />
                                    {this.renderLightbox(content.field_instr_image)}
                                </div>
                            )
                        }

                        <div className={contentClass}>
                            {isUser && (
                                <div>
                                    <h1>{(content as User).name.value}</h1>
                                    {contentHtml && <div className="User-header">Biography:</div>}
                                </div>
                            )}
                            {(isGroup || isNews || isVintageGuitar) && <div className="Content-title">{content.title}</div>}
                            {
                                contentImgUrl &&
                                    <FailsafeImg
                                        className="Content-image"
                                        src={contentImgUrl}
                                        alt={contentImgUrl}
                                    />
                            }
                            <EmbeddedContent contentHtml={contentHtml} />
                            {isGroup && this.instrumentGroupRender(content as InstrumentGroupNode)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private instrumentGroupRender(groupItems: InstrumentGroupNode) {
        return (
            <InstrumentGroup
                instrumentGroup={groupItems}
                isMobile={this.props.isMoble}
            />);
    }

    private loadData() {
        let targetAlias = this.props.location.pathname.slice(1);
        if (targetAlias.endsWith('/')) targetAlias = targetAlias.slice(0, -1);

        if (targetAlias.startsWith('users/')) {
            return UsersApi.getUser(targetAlias).then((content: User) => {
                this.setState({ content, contentLoaded: true });
            });
        }

        // Check if this is a SKU
        if (targetAlias.match(/^[A-Za-z0-9]{3,}$/)) {
            return AliasApi.getInstrumentAliasBySku(targetAlias).then((alias) => {
                history.push(alias.alias);
            }).catch(() => this.getNodeByAlias(targetAlias));
        }

        this.getNodeByAlias(targetAlias);
    }

    private getNodeByAlias(targetAlias: string) {
        NodeApi.getNode(targetAlias).then((content: Page | Article | InstrumentGroupNode | VintageGuitarArticle) => {
            this.setState({ content, contentLoaded: true });
        }).catch(() => {
            history.push('/');
        });
    }
}

export default DefaultPage;



// WEBPACK FOOTER //
// ./src/components/pages/default/DefaultPage.tsx